import { http, HttpResponse } from 'msw/http'
import { createMockId, mockData } from './mockData'
import type {
  CreatePlanLegislation,
  LegislationDocument,
  PlanLegislation
} from '../types/types'

const labels: Record<string, string> = {
  'kapittel-1': 'Kapittel I. Formål, virkeområde og definisjoner',
  'kapittel-1-paragraf-1':
    'Kapittel I. Formål, virkeområde og definisjoner, § 1',
  'kapittel-1-paragraf-1-ledd-1':
    'Kapittel I. Formål, virkeområde og definisjoner, § 1, avsnitt 1',
  'kapittel-1-paragraf-1-ledd-2':
    'Kapittel I. Formål, virkeområde og definisjoner, § 1, avsnitt 2',
  'kapittel-1-paragraf-1-ledd-3':
    'Kapittel I. Formål, virkeområde og definisjoner, § 1, avsnitt 3'
}

export const legislationFixture: LegislationDocument = {
  legislation: {
    documentId: 'NL/lov/2003-12-19-124',
    code: 'LOV-2003-12-19-124',
    title: 'Lov om matproduksjon og mattrygghet mv. (matloven)',
    shortTitle: 'Matloven'
  },
  html: '<main class="documentBody" id="dokument"><h1>Matloven</h1><section class="section" id="kapittel-1"><h2>Kapittel I. Formål, virkeområde og definisjoner</h2><article class="legalArticle" id="kapittel-1-paragraf-1"><h3 class="legalArticleHeader"><span class="legalArticleValue">&sect; 1</span>. Formål</h3><article class="legalP" id="kapittel-1-paragraf-1-ledd-1">Formålet med loven er å sikre helsemessig trygge næringsmidler.</article><article class="legalP" id="kapittel-1-paragraf-1-ledd-2">Loven skal videre fremme god plante- og dyrehelse.</article><article class="legalP" id="kapittel-1-paragraf-1-ledd-3">Loven skal også ivareta hensynet til aktørene langs hele produksjonskjeden.</article></article></section></main>',
  availablePartIds: Object.keys(labels)
}

function visibleEntries(planId: number) {
  const ids = new Set<number>()
  let plan = mockData.plans.plans.find((item) => item.id === planId)
  while (plan && !ids.has(plan.id)) {
    ids.add(plan.id)
    const parentId: number | undefined = plan.parents[0]
    plan = mockData.plans.plans.find((item) => item.id === parentId)
  }
  return mockData.planLegislation.filter((entry) => ids.has(entry.sourcePlanId))
}

export const legislationHandlers = [
  http.get('/api/legislation', () =>
    HttpResponse.json({
      documents: [legislationFixture.legislation]
    })
  ),
  http.get('/api/legislation/preview', ({ request }) =>
    new URL(request.url).searchParams.get('documentId') ===
    legislationFixture.legislation.documentId
      ? HttpResponse.json(legislationFixture)
      : new HttpResponse(null, { status: 404 })
  ),
  http.get('/api/plans/:planId/legislation', ({ params }) =>
    HttpResponse.json(visibleEntries(Number(params.planId)))
  ),
  http.post('/api/plans/:planId/legislation', async ({ params, request }) => {
    const planId = Number(params.planId)
    const plan = mockData.plans.plans.find((item) => item.id === planId)
    if (!plan) return new HttpResponse(null, { status: 404 })
    const body = (await request.json()) as CreatePlanLegislation
    if (
      body.documentId !== legislationFixture.legislation.documentId ||
      body.selectedPartIds.some(
        (id) => !legislationFixture.availablePartIds.includes(id)
      )
    ) {
      return new HttpResponse(null, { status: 400 })
    }
    const id = createMockId()
    const entry: PlanLegislation = {
      id,
      sourcePlanId: planId,
      sourcePlanName: plan.description ?? '',
      legislation: legislationFixture.legislation,
      addedAt: new Date().toISOString(),
      selectedParts: body.selectedPartIds.map((id) => ({
        id,
        label: labels[id]
      })),
      etag: `legislation-${id}`
    }
    mockData.planLegislation.push(entry)
    return HttpResponse.json(entry, {
      status: 201,
      headers: {
        Location: `/plans/${planId}/legislation/${id}`,
        ETag: entry.etag
      }
    })
  }),
  http.delete(
    '/api/plans/:planId/legislation/:entryId',
    ({ params, request }) => {
      const index = mockData.planLegislation.findIndex(
        (entry) =>
          entry.sourcePlanId === Number(params.planId) &&
          entry.id === Number(params.entryId)
      )
      if (index < 0) return new HttpResponse(null, { status: 404 })
      if (
        request.headers.get('If-Match') !== mockData.planLegislation[index].etag
      )
        return new HttpResponse(null, { status: 412 })
      mockData.planLegislation.splice(index, 1)
      return new HttpResponse(null, { status: 204 })
    }
  )
]
